// Modified from Dawn's MenuDrawer component
class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));

    this.handleSummaryClick = this.onSummaryClick.bind(this);
    this.handleCloseButtonClick = this.onCloseButtonClick.bind(this);

    this.bindEvents();

    if (this.dataset.breakpoint) {
      const breakpoint = parseInt(this.dataset.breakpoint);
      window.addEventListener("resize", (event) => {
        if (window.innerWidth >= breakpoint && this.mainDetailsToggle.open) {
          this.closeMenuDrawer(event);
        }
      });
    }
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.handleSummaryClick)
    );
    this.querySelectorAll("button:not(.localization-selector)").forEach(
      (button) => button.addEventListener("click", this.handleCloseButtonClick)
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  toggleMainMenu() {
    const mainSummary = this.mainDetailsToggle.querySelector("summary");
    if (mainSummary) {
      mainSummary.click();
    }
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (!isOpen) {
      // Reset scroll to top of menu
      const innerContainer = detailsElement.querySelector(
        ".menu-drawer__inner-container"
      );
      if (innerContainer) {
        setTimeout(() => (innerContainer.scrollTop = 0));
      }
    }

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      console.log("SUMMARY CLICK TRIGGERING OPEN", isOpen);
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    }, 100);
    summaryElement.setAttribute("aria-expanded", true);
    console.log("OPEN DRAWER TRAP FOCUS");
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add("overflow-hidden");
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove("overflow-hidden");
    removeTrapFocus(elementToFocus);

    if (
      event.currentTarget.classList &&
      event.currentTarget.classList.contains("date-picker-modal-opener")
    ) {
      this.closeDetailsElement(this.mainDetailsToggle);
    } else {
      this.closeAnimation(this.mainDetailsToggle);
    }

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    if (detailsElement === this.mainDetailsToggle) {
      this.closeMenuDrawer(event);
    } else {
      this.closeSubmenu(detailsElement);
    }
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeDetailsElement(detailsElement) {
    detailsElement.removeAttribute("open");
    if (detailsElement.closest("details[open]")) {
      trapFocus(
        detailsElement.closest("details[open]"),
        detailsElement.querySelector("summary")
      );
    }
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.closeDetailsElement(detailsElement);
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class MenuOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", (e) => {
      // Open menu and optionally scroll to submenu
      const menu = document.querySelector(this.getAttribute("data-menu"));
      if (!menu) return;
      menu.toggleMainMenu();
      const submenu =
        this.dataset.submenu && menu.querySelector(this.dataset.submenu);
      if (!submenu) return;
      setTimeout(() => {
        submenu.scrollIntoView();
        const focusable = getFocusableElements(submenu)[0] || submenu;
        focusable.focus();
      }, 10);
    });
  }
}
customElements.define("menu-opener", MenuOpener);
